import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Send, Crown } from "lucide-react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { postWithRetry } from "@/lib/retryablePost";
import { useAuth } from "@/context/AuthContext";

// MotivationCoachScreen — conversational AI coach.
// iMessage-style layout: scrollable thread + docked composer that tracks the
// soft keyboard via visualViewport so it ALWAYS stays visible above the
// keyboard. Bottom nav is hidden on this route (see App.js NO_NAV_PATTERNS).

const STARTERS = [
  "I've been feeling kind of stuck this week.",
  "Hype me up — I'm about to hit the gym.",
  "Help me plan today.",
  "I keep missing my routine. Why?",
  "Tell me one thing I'm doing right.",
];

export default function MotivationCoachScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = !!user?.premium;

  const [sessionId, setSessionId] = useState(() =>
    sessionStorage.getItem("motivate_sid") || null
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // Keyboard-aware composer state — see useEffects below.
  const [kbHeight, setKbHeight] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch history if we have a saved session
  useEffect(() => {
    if (sessionId) {
      api.get(`/coach/motivate/${sessionId}`).then((r) => {
        setMessages(r.data.messages || []);
      }).catch(() => {});
    }
  }, [sessionId]);

  // Pin scroll to the latest bubble whenever messages or typing state changes
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  // Hide the BottomNav app-wide while on this route (App.js already does this
  // via NO_NAV_PATTERNS for /coach + /improve/motivate, but we also add the
  // body class so the keyboard-open CSS path works identically to CoachScreen).
  useEffect(() => {
    document.body.classList.add("coach-route");
    return () => {
      document.body.classList.remove("coach-route");
      document.body.classList.remove("kb-open");
    };
  }, []);

  // Subscribe to visualViewport on iOS Safari + Chrome Android. On resize we
  // compute the keyboard overlap = innerHeight - vv.height - vv.offsetTop
  // and store it as state. Inline style is more reliable on iOS WKWebView
  // than CSS-var propagation.
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return undefined;
    const recalc = () => {
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbHeight(overlap);
      document.documentElement.style.setProperty("--kb-h", `${overlap}px`);
    };
    recalc();
    vv.addEventListener("resize", recalc);
    vv.addEventListener("scroll", recalc);
    return () => {
      vv.removeEventListener("resize", recalc);
      vv.removeEventListener("scroll", recalc);
      document.documentElement.style.setProperty("--kb-h", "0px");
    };
  }, []);

  // Body class toggle so iOS Safari and other quirky webviews can hide the
  // nav once the keyboard opens, even before visualViewport reports it.
  useEffect(() => {
    if (kbHeight > 0 || inputFocused) {
      document.body.classList.add("kb-open");
    } else {
      document.body.classList.remove("kb-open");
    }
  }, [kbHeight, inputFocused]);

  // iOS Safari fires the resize ~250-700ms AFTER the keyboard finishes
  // animating. Triple-sample to catch the late fire.
  const onInputFocus = () => {
    setInputFocused(true);
    const recheck = () => {
      const vv = window.visualViewport;
      if (vv) {
        const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setKbHeight(overlap);
      }
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      inputRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    };
    setTimeout(recheck, 120);
    setTimeout(recheck, 350);
    setTimeout(recheck, 700);
  };
  const onInputBlur = () => setInputFocused(false);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setError("");
    setSending(true);
    setInput("");
    const now = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", text, at: now }]);
    try {
      const payload = { message: text };
      if (sessionId) payload.session_id = sessionId;
      const data = await postWithRetry("/coach/motivate", payload, { timeout: 45_000, scope: "coach" });
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        sessionStorage.setItem("motivate_sid", data.session_id);
      }
      setMessages((m) => [...m, { role: "coach", text: data.reply, at: new Date().toISOString() }]);
      try { track("feature_completed", { feature: "coach", sub_action: "message_exchanged" }); } catch { /* ignore */ }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429 && detail?.error?.message) {
        setError(detail.error.message);
      } else if (status === 503) {
        setError("Coach is briefly offline. Try again in a moment.");
      } else {
        setError("Something hiccuped — try sending that again.");
      }
      setMessages((m) => m.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const newSession = () => {
    sessionStorage.removeItem("motivate_sid");
    setSessionId(null);
    setMessages([]);
    setError("");
  };

  // Inline composer styling. `bottom` floats the composer to sit just above the
  // keyboard when one is open, or 0 when it isn't.
  const composerStyle = {
    bottom: kbHeight > 0 ? `${kbHeight}px` : "0px",
    paddingBottom: kbHeight > 0 ? "8px" : "calc(8px + env(safe-area-inset-bottom, 0px))",
  };

  return (
    <div className="screen motivate-screen-wrapper fade-in" data-testid="motivate-screen">
      <div className="top-bar motivate-top-bar">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back" data-testid="motivate-back-btn">
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">COACH</div>
        {isPremium ? (
          <span className="premium-pill" data-testid="motivate-quota-pill" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Crown size={10} strokeWidth={2} /> PREMIUM
          </span>
        ) : (
          <span className="premium-pill" data-testid="motivate-quota-pill">FREE · 2/DAY</span>
        )}
      </div>

      {messages.length === 0 ? (
        <div className="motivate-empty" data-testid="motivate-empty">
          <div className="eyebrow" style={{ marginBottom: 8 }}>AI MOTIVATION COACH</div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 12, lineHeight: 1.12 }}>
            Your hype.<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Your accountability.</em>
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 18 }}>
            Tell me what&apos;s on your mind — wins, slumps, anything. I&apos;ll meet you there.
          </p>
          <div className="eyebrow" style={{ marginBottom: 8 }}>START WITH</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {STARTERS.map((s, i) => (
              <button
                key={i}
                type="button"
                className="card"
                onClick={() => send(s)}
                disabled={sending}
                data-testid={`motivate-starter-${i}`}
                style={{ padding: 12, textAlign: "left", width: "100%", cursor: "pointer" }}
              >
                <div className="body-sm" style={{ color: "var(--text)" }}>{s}</div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div ref={scrollRef} className="motivate-thread" data-testid="motivate-thread">
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={`motivate-msg-${i}`}
              style={{
                display: "flex",
                justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                marginBottom: 10,
              }}
            >
              <div
                className={`motivate-bubble ${m.role === "user" ? "is-user" : "is-coach"}`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {sending && (
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }} data-testid="motivate-typing">
              <div className="motivate-bubble is-coach is-typing">
                <span className="dots-anim">Coach is typing</span>
              </div>
            </div>
          )}
        </div>
      )}

      <form
        className="motivate-composer"
        style={composerStyle}
        onSubmit={(e) => { e.preventDefault(); send(); }}
        data-kb-open={(kbHeight > 0 || inputFocused) ? "true" : "false"}
      >
        {error && (
          <div className="motivate-error" data-testid="motivate-error">
            {error}
          </div>
        )}
        <div className="motivate-composer-row">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, 1200))}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Tell me what's going on…"
            rows={1}
            className="input motivate-input"
            data-testid="motivate-input"
          />
          <button
            type="submit"
            className="btn btn-gold motivate-send"
            disabled={!input.trim() || sending}
            aria-label="Send"
            data-testid="motivate-send-btn"
          >
            {sending ? <span className="spinner" /> : <Send size={16} strokeWidth={1.8} />}
          </button>
        </div>
        {messages.length > 0 && !inputFocused && (
          <button
            type="button"
            onClick={newSession}
            className="motivate-new-session"
            data-testid="motivate-new-session"
          >
            Start a new conversation
          </button>
        )}
      </form>
    </div>
  );
}
